import {
    DeliverableState,
    ProjectState,
    ProjectStatus,
    type Project,
    type Tag,
    type TagValue,
} from '@/gql/graphql';
import { filterParsers } from '@/utils/portfolio';
import type {
    FrameworkAction,
    FrameworkArea,
    FrameworkAxesEntity,
    FrameworkData,
    FrameworkFocus,
    FrameworkProjectPin,
} from '@/types/framework';
import { useSessionStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, reactive, ref, watchEffect, nextTick } from 'vue';

export type ProjectPinColors = {
    type: string;
    status: string;
    department?: string;
    program?: string;
};

export interface PortfolioFilters {
    projectStatus: ProjectStatus[];
    projectState: ProjectState[];
    deliverableState: DeliverableState[];
    programs: string[];
    projectManagers: string[];
    departments: string[];
    themes: string[];
    tagValues: string[];
}

export type FrameworkProjectPinExtended = FrameworkProjectPin & {
    colors: ProjectPinColors;
    highlight: boolean;
    inactive: boolean;
};

export type FrameworkDataExtended = Omit<FrameworkData, 'pins'> & {
    name: string;
    pins: FrameworkProjectPinExtended[];
};

const initialState = {
    name: '',
    organisation: {
        logoUrl: undefined,
        name: '',
    },
    axes: {
        x: {
            labels: [],
            roles: [],
            actions: [],
            focus: [],
        },
        y: {
            // tooltipLabel: '',
            labels: [],
            drivers: [],
            groups: [],
            needs: [],
        },
    },
    pins: [],
    programs: [],
    themes: [],
} satisfies FrameworkDataExtended;

export type FrameworkViewMode = 'department' | 'program' | 'type' | 'status';

export type PinTypeColors = {
    [K in ProjectStatus]: string;
};

export type PinStatusColors = {
    [K in
        | 'pending'
        | 'on_hold'
        | 'bottleneck'
        | 'escalation'
        | 'finished']: string;
};

export interface FrameworkSettings {
    cellHeight: number;
    pinColors: {
        irrelevant: string;
        type: PinTypeColors;
        status: PinStatusColors;
    };
}

export interface FrameworkState {
    isReady: boolean;
    isLoading: boolean;
    isEditing: boolean;
    viewMode: FrameworkViewMode;
}

export type FrameworkMode = 'view' | 'edit';

type FrameworkViewKeys = {
    [K in FrameworkViewMode]: {
        color?: string;
        name?: string;
    };
};

export type PinColorProject = Pick<
    Project,
    | 'uuid'
    | 'status'
    | 'finished'
    | 'state'
    | 'deliverableState'
    | 'onHold'
    | 'programs'
    | 'departments'
>;

export interface FrameworkProject {
    displayData: FrameworkViewKeys;
}

export type PinFilter = {
    type: ProjectStatus;
    status: ProjectState;
    deliverableStatus: DeliverableState;
    programId?: string;
    projectManagerId?: string;
    departmentId?: string;
    tagIds: string[];
    themeIds: string[];
};

export const useFrameworkStore = defineStore('framework', () => {
    const framework = reactive<FrameworkDataExtended>(initialState);
    const projects = ref(new Map<string, FrameworkViewKeys>());
    const loading = ref(false);

    function sortYAxis(axis: FrameworkData['axes']['y']) {
        const { drivers, groups, needs } = axis;

        drivers.sort((a, b) => a.position - b.position);
        groups.sort((a, b) => a.position - b.position);

        needs.sort((a, b) => {
            return a.position - b.position;
        });
    }

    function sortActionsAndRoles(roles: FrameworkAxesEntity[]) {
        const sortedActions: FrameworkAction[] = [];
        const sortedFocus: FrameworkFocus[] = [];

        roles.sort((a, b) => a.position - b.position);

        roles.forEach((role) => {
            const actions = framework.axes.x.actions
                .filter((a) => a.roleId === role.id)
                .sort((a, b) => a.position - b.position);
            sortedActions.push(...actions);
        });

        framework.axes.x.actions = sortedActions;

        sortedActions.forEach((action, index) => {
            const f = framework.axes.x.focus.find(
                (fo) => fo.actionId === action.id
            );
            if (!f) return;

            sortedFocus[index] = f;
        });

        framework.axes.x.focus = sortedFocus;
    }

    function initialize(data: FrameworkDataExtended) {
        reset();

        nextTick(() => {
            Object.assign(framework, data);
            sortActionsAndRoles(framework.axes.x.roles);
            sortYAxis(framework.axes.y);
            state.isReady = true;
        });
    }

    function setFrameworkName(name: string) {
        framework.name = name;
    }

    function reset() {
        Object.assign(framework, initialState);
        state.isReady = false;
    }

    // Filters
    const filtersPortfolioId = ref<string | undefined>(undefined);

    // List of all tags + tagValues
    const filterTags = ref<Tag[]>([]);

    // Current filter state
    const filters = reactive<PortfolioFilters>({
        projectStatus: [],
        projectState: [],
        deliverableState: [],
        programs: [],
        projectManagers: [],
        departments: [],
        themes: [],
        tagValues: [],
    });

    const filterCount = computed(() => {
        return Object.values(filters).reduce((acc, arr) => {
            return acc + arr.length;
        }, 0);
    });

    function resetFilters() {
        Object.keys(filters).forEach((k) => {
            filters[k as keyof PortfolioFilters].length = 0;
        });

        filtersPortfolioId.value = undefined;
    }

    function addFilterItem<K extends keyof PortfolioFilters>(
        key: K,
        value: PortfolioFilters[K][number]
    ): void {
        (filters[key] as Array<typeof value>).push(value);
    }

    function addFilterFromQuery(
        key: keyof PortfolioFilters,
        raw: string
    ): void {
        const value = filterParsers[key](raw);
        if (value !== null) addFilterItem(key, value);
    }

    // Framework settings
    const settings = reactive<FrameworkSettings>({
        cellHeight: 56,
        pinColors: {
            irrelevant: '#888',
            type: {
                approved_idea: '#099CED',
                idea: '#642C91',
                project: '#479243',
            },
            status: {
                pending: '#466261',
                on_hold: '#099CED',
                bottleneck: '#FEAB11',
                escalation: '#C00000',
                finished: '#5E5D62',
            },
        },
    });

    // Framework working state
    const state = reactive<FrameworkState>({
        isReady: false,
        isLoading: false,
        isEditing: false,
        viewMode: 'type',
    });

    const mode = ref<FrameworkMode>('view');
    const pins = computed(() => framework.pins);
    const focus = computed(() => {
        const sorted: FrameworkFocus[] = [];

        actions.value.forEach((action, index) => {
            const f = framework.axes.x.focus.find(
                (fo) => fo.actionId === action.id
            );
            if (!f) return;

            sorted[index] = f;
        });

        return sorted;
    });
    const roles = computed(() => framework.axes.x.roles);
    const needs = computed(() => framework.axes.y.needs);
    const actions = computed(() => {
        const sorted: FrameworkAction[] = [];
        roles.value.forEach((role) => {
            const actions = framework.axes.x.actions
                .filter((a) => a.roleId === role.id)
                .sort((a, b) => a.position - b.position);
            sorted.push(...actions);
        });

        return sorted;
    });
    const rowCount = computed(() => framework.axes.y.needs.length ?? 0);
    const columnCount = computed(() => framework.axes.x.actions.length ?? 0);

    const mostProjectsPerCell = computed(() => {
        const cellCounts = new Map<string, number>();

        for (const pin of framework.pins) {
            const key = `${pin.parents.x}:${pin.parents.y}`;
            cellCounts.set(key, (cellCounts.get(key) ?? 0) + 1);
        }

        let most = 0;
        for (const count of cellCounts.values()) {
            if (count > most) most = count;
        }

        return most;
    });

    watchEffect(() => {
        state.isEditing = mode.value === 'edit';
    });

    function setLoading(b: boolean) {
        state.isLoading = b;
    }

    function setViewMode(mode: FrameworkViewMode) {
        state.viewMode = mode;
    }

    function setFrameworkMode(fwmode: FrameworkMode) {
        mode.value = fwmode;
        state.isEditing = fwmode === 'edit';
    }

    const pinFilterInformation = ref(
        new Map<
            string,
            {
                type: ProjectStatus;
                status: ProjectState;
                deliverableStatus: DeliverableState;
                programId?: string;
                projectManagerId?: string;
                departmentId?: string;
                tagIds: string[];
                themeIds: string[];
            }
        >()
    );

    const filteredPinIds = computed(() => {
        const result = new Set<string>();

        const {
            projectStatus,
            projectState,
            deliverableState,
            programs,
            projectManagers,
            departments,
            tagValues,
            themes,
        } = filters;

        // Pre-compute selected tag IDs per tag group for tag matching
        const tagGroups: Set<string>[] = [];
        if (tagValues.length > 0) {
            const selectedSet = new Set(tagValues);
            for (const tag of filterTags.value) {
                const selected = tag.values
                    .filter((tv: TagValue) => selectedSet.has(tv.id))
                    .map((tv: TagValue) => tv.id);
                if (selected.length > 0) {
                    tagGroups.push(new Set(selected));
                }
            }
        }

        // When filtering by BOTTLENECK, also include projects with ESCALATION
        const deliverableStateSet = new Set(deliverableState);
        if (deliverableStateSet.has(DeliverableState.BOTTLENECK)) {
            deliverableStateSet.add(DeliverableState.ESCALATION);
        }

        for (const [pinId, pinfo] of pinFilterInformation.value) {
            if (projectStatus.length > 0 && !projectStatus.includes(pinfo.type))
                continue;
            if (projectState.length > 0 && !projectState.includes(pinfo.status))
                continue;
            if (
                deliverableStateSet.size > 0 &&
                (pinfo.deliverableStatus === undefined ||
                    !deliverableStateSet.has(pinfo.deliverableStatus))
            )
                continue;
            if (
                programs.length > 0 &&
                !(programs.includes('empty') && pinfo.programId === undefined) &&
                (pinfo.programId === undefined ||
                    !programs.includes(pinfo.programId))
            )
                continue;
            if (
                projectManagers.length > 0 &&
                !(projectManagers.includes('empty') && pinfo.projectManagerId === undefined) &&
                (pinfo.projectManagerId === undefined ||
                    !projectManagers.includes(pinfo.projectManagerId))
            )
                continue;
            if (
                departments.length > 0 &&
                !(departments.includes('empty') && pinfo.departmentId === undefined) &&
                (pinfo.departmentId === undefined ||
                    !departments.includes(pinfo.departmentId))
            )
                continue;

            // Check tags: every tag group must have at least one match
            let tagsMatch = true;
            for (const group of tagGroups) {
                if (!pinfo.tagIds.some((id) => group.has(id))) {
                    tagsMatch = false;
                    break;
                }
            }
            if (!tagsMatch) continue;

            if (
                themes.length > 0 &&
                !(themes.includes('empty') && pinfo.themeIds.length === 0) &&
                !pinfo.themeIds.some((id) => themes.includes(id))
            )
                continue;

            result.add(pinId);
        }

        return result;
    });

    function inFilters(pinId: string): boolean {
        return filteredPinIds.value.has(pinId);
    }

    function addPins(pins: FrameworkProjectPinExtended[]) {
        framework.pins = [...framework.pins, ...pins];
    }

    function addThemes(themes: FrameworkArea[]) {
        framework.themes = [...framework.themes, ...themes];
    }

    function getPinColor(id: string, viewMode: FrameworkViewMode) {
        return (
            projects?.value?.get(id)?.[viewMode]?.color ??
            settings.pinColors.irrelevant
        );
    }

    function getPinLegend(id: string, viewMode: FrameworkViewMode) {
        return projects.value.get(id)?.[viewMode];
    }

    function getFocusFromCellIndex(index: number) {
        const { col } = getIndicesFromIndex(index);

        return focus.value[col];
    }
    function getNeedFromCellIndex(index: number) {
        const { row } = getIndicesFromIndex(index);

        return needs.value[row];
    }

    function getIndicesFromIndex(index: number) {
        return {
            row: Math.floor(index / columnCount.value),
            col: index % columnCount.value,
        };
    }

    function getIndexFromIndices(row: number, col: number) {
        const index = row * columnCount.value + col;
        return index;
    }

    function getPinCellIndex(pinId: string): number {
        const pin = pins.value.find((p) => p.id === pinId);
        if (!pin) return Infinity;

        const col = focus.value.findIndex((f) => f.id === pin.parents.x);
        const row = needs.value.findIndex((n) => n.id === pin.parents.y);
        if (col === -1 || row === -1) return Infinity;

        return getIndexFromIndices(row, col);
    }

    return {
        actions,
        addFilterItem,
        addFilterFromQuery,
        addPins,
        addThemes,
        columnCount,
        filterCount,
        filters,
        filtersPortfolioId,
        filterTags,
        focus,
        framework,
        getIndicesFromIndex,
        getIndexFromIndices,
        getFocusFromCellIndex,
        getPinCellIndex,
        getNeedFromCellIndex,
        getPinColor,
        getPinLegend,
        inFilters,
        initialize,
        loading,
        mode,
        mostProjectsPerCell,
        needs,
        pins,
        pinFilterInformation,
        projects,
        reset,
        resetFilters,
        roles,
        rowCount,
        settings,
        setLoading,
        setFrameworkMode,
        setFrameworkName,
        setViewMode,
        state,
    };
});

export const useFrameworkProjectStore = defineStore('framework-project', () => {
    const projects = ref<{ name: string; id: string; tag: string }[]>([]);
    const highlightedProjectId = ref<undefined | string>(undefined);

    function set(p: { name: string; id: string; tag: string }[]) {
        projects.value = p;
    }

    function highlightProject(id: string) {
        highlightedProjectId.value = id;
    }

    function removeHighlight() {
        highlightedProjectId.value = undefined;
    }

    return {
        highlightedProjectId,
        projects,
        set,
        highlightProject,
        removeHighlight,
    };
});

type PanelName = 'projects';

export const useFrameworkUIStore = defineStore('framework-ui', () => {
    const panels = useSessionStorage<{
        projects: boolean;
    }>('framework-ui-panels', {
        projects: true,
    });

    // Zoom level as percentage (100 = 100%)
    const zoomLevel = ref(100);

    function setZoomLevel(level: number) {
        zoomLevel.value = level;
    }

    function togglePanel(panel: PanelName) {
        _setPanel(panel, !panels.value[panel]);
    }

    function showProjectsPanel() {
        _setPanel('projects', true);
    }

    function hideProjectsPanel() {
        _setPanel('projects', false);
    }

    function _setPanel(panel: PanelName, value: boolean) {
        switch (panel) {
            case 'projects':
                panels.value.projects = value;
        }
    }

    return {
        panels,
        zoomLevel,
        setZoomLevel,
        togglePanel,
        showProjectsPanel,
        hideProjectsPanel,
    };
});
